import { FilePlus2 } from "lucide-react";
import { Link } from "react-router-dom";
import { DataTable } from "../components/DataTable";
import { Badge, Button, Card, EmptyState, Skeleton } from "../components/ui";
import { useContracts } from "../data/platform-data";
import { useAssetContracts } from "../data/assets-platform-data";
import { useTenantRuntime } from "../config/TenantRuntime";
import { useFlowAgreements } from "../data/flow-data";

export function ContractsPage() {
  const { hasModule, tenant } = useTenantRuntime();
  const isFlow = tenant.product.commercialProductCode === "EVA_FLOW";
  const assetVertical = hasModule("assets") && !isFlow;
  const query = useContracts();
  const assetQuery = useAssetContracts();
  const flowQuery = useFlowAgreements(isFlow);

  if (isFlow) {
    return (
      <Card
        title="Contratos"
        description="Acordos de cobrança de qualquer produto ou serviço, da Yatta ou do parceiro."
      >
        {flowQuery.isLoading && <Skeleton className="table-skeleton" />}
        {flowQuery.error && (
          <EmptyState title="Falha ao carregar contratos" description={flowQuery.error.message} />
        )}
        {!flowQuery.isLoading && !flowQuery.error && (flowQuery.data?.length ?? 0) === 0 && (
          <EmptyState
            title="Nenhum contrato cadastrado"
            description="A carteira geral ainda não tem contratos neste ambiente."
          />
        )}
        {!flowQuery.isLoading && !flowQuery.error && (flowQuery.data?.length ?? 0) > 0 && (
          <DataTable
            rows={flowQuery.data ?? []}
            getKey={(row) => row.id}
            columns={[
              { key: "number", header: "Contrato", render: (row) => row.number },
              { key: "type", header: "Modelo", render: (row) => row.type },
              { key: "origin", header: "Origem", render: (row) => row.origin },
              {
                key: "status",
                header: "Status",
                render: (row) => (
                  <Badge tone={row.status === "ACTIVE" ? "success" : "warning"}>{row.status}</Badge>
                ),
              },
              { key: "updated", header: "Atualização", render: (row) => row.updatedAt },
            ]}
          />
        )}
      </Card>
    );
  }

  if (assetVertical) {
    return (
      <Card
        title="Contratos patrimoniais"
        description="Venda de veículos, locação de imóveis e locação de veículos."
        action={
          <Link to="/operacoes/nova?tipo=vehicle-sale">
            <Button>
              <FilePlus2 size={16} /> Novo contrato
            </Button>
          </Link>
        }
      >
        {assetQuery.isLoading && <Skeleton className="table-skeleton" />}
        {assetQuery.error && (
          <EmptyState title="Falha ao carregar contratos" description={assetQuery.error.message} />
        )}
        {!assetQuery.isLoading && !assetQuery.error && (
          <DataTable
            rows={assetQuery.data ?? []}
            getKey={(contract) => contract.number}
            columns={[
              {
                key: "number",
                header: "Número",
                render: (contract) => contract.number,
              },
              {
                key: "customer",
                header: "Cliente",
                render: (contract) => contract.customer,
              },
              {
                key: "type",
                header: "Tipo",
                render: (contract) => contract.type,
              },
              {
                key: "asset",
                header: "Ativo",
                render: (contract) => contract.asset,
              },
              {
                key: "status",
                header: "Status",
                render: (contract) => (
                  <Badge tone={contract.status === "ACTIVE" ? "success" : "warning"}>
                    {contract.status}
                  </Badge>
                ),
              },
              {
                key: "updated",
                header: "Atualização",
                render: (contract) => contract.updatedAt,
              },
            ]}
          />
        )}
      </Card>
    );
  }

  return (
    <Card
      title="Contratos e evidências"
      description="Documentos assinados são imutáveis e preservam snapshots."
      action={
        <Button>
          <FilePlus2 size={16} /> Gerar contrato
        </Button>
      }
    >
      {query.isLoading && <Skeleton className="table-skeleton" />}
      {query.error && (
        <EmptyState title="Falha ao carregar contratos" description={query.error.message} />
      )}
      {!query.isLoading && !query.error && (
        <DataTable
          rows={query.data ?? []}
          getKey={(contract) => contract.number}
          columns={[
            {
              key: "number",
              header: "Número",
              render: (contract) => contract.number,
            },
            {
              key: "customer",
              header: "Cliente",
              render: (contract) => contract.customer,
            },
            {
              key: "type",
              header: "Tipo",
              render: (contract) => contract.type,
            },
            {
              key: "status",
              header: "Status",
              render: (contract) => (
                <Badge
                  tone={
                    contract.status === "ACTIVE" || contract.status === "ATIVO"
                      ? "success"
                      : "warning"
                  }
                >
                  {contract.status}
                </Badge>
              ),
            },
            {
              key: "version",
              header: "Versão",
              render: (contract) => contract.version,
            },
            {
              key: "updated",
              header: "Atualização",
              render: (contract) => contract.updatedAt,
            },
          ]}
        />
      )}
    </Card>
  );
}
